import { BaseSyntheticEvent } from 'react';
import { TFunction, useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import { queryClient } from 'queryClient';
import { LEAD_GQL_QUERY_BASE } from 'components/domain/lead/useFetchLead';
import useCreateSecurityDepositCharge from 'components/domain/transaction/useCreateSecurityDepositCharge';
import FormWithProvider from 'components/forms/form/Form';
import { FormContextParts } from 'components/forms/form/Form.types';
import useAppModal from 'hooks/useAppModal';
import { Lead } from 'models/Leads';
import { TransactionStatus, TransactionType } from 'models/Transactions';
import { normalizeKey } from 'utils/localeUtils';
import {
  AdditionalChargeFormValues,
  AdditionalChargeModalParams,
} from './AdditionalChargeModal.types';
import ChargeModalFormElements from './ChargeModalFormElements';

const additionalChargeFormSchema = (maxAmount: number, t: TFunction) => ({
  amount: Yup.number()
    .positive()
    .max(
      maxAmount,
      t(
        'pageOrder.transactions.actions.additionalCharge.error.maxAmountExceeded',
        { maxAmount },
      ),
    )
    .required(),
});

function getMaxAmount(lead: Lead) {
  const {
    // @ts-expect-error TS2339 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
    order: { securityDeposit, transactions },
  } = lead;

  const alreadyChargedAmount =
    transactions
      ?.filter(
        // @ts-expect-error TS7031 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
        ({ status, type }) =>
          status === TransactionStatus.SUCCESS &&
          type === TransactionType.SECURITY_DEPOSIT_CHARGE,
      )
      // @ts-expect-error TS7006 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
      ?.reduce((acc, { amount }) => acc + amount, 0) || 0;

  return Math.max(securityDeposit - alreadyChargedAmount, 0);
}

const AdditionalChargeModal = ({
  modalId,
  params: { lead },
}: {
  modalId: string;
  params: AdditionalChargeModalParams;
}) => {
  const { closeModal } = useAppModal();
  const { createSecurityDepositCharge } = useCreateSecurityDepositCharge();
  const { t } = useTranslation();

  const {
    // @ts-expect-error TS2339 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
    order: { uid: orderUid },
    uid: leadUid,
  } = lead;

  const onCancel = () => {
    closeModal(modalId);
  };

  const onSubmit = async (
    { amount }: AdditionalChargeFormValues,
    _event: BaseSyntheticEvent | undefined,
    { setError }: FormContextParts<AdditionalChargeFormValues>,
  ) => {
    await createSecurityDepositCharge({
      payload: {
        amount,
        orderUid,
      },
      successCallback: () => {
        queryClient.invalidateQueries([LEAD_GQL_QUERY_BASE, leadUid]);
        closeModal(modalId);
      },
      // @ts-expect-error TS2322 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
      errorCallback: ({ response: { data } }) => {
        const errorCode = data?.apiErrorCode;

        setError('root', {
          message: t([
            normalizeKey(
              `pageOrder.transactions.actions.additionalCharge.error.api.${errorCode}`,
            ),
            'common.apiResponses.internalServerError',
          ]),
        });
        queryClient.invalidateQueries([LEAD_GQL_QUERY_BASE, leadUid]); // invalidate to display possible created failed transactions
      },
    });
  };

  const maxAmount = getMaxAmount(lead);

  const formDefaultValues = {
    amount: maxAmount,
  };

  return (
    <FormWithProvider
      defaultValues={formDefaultValues}
      noValidate
      onSubmit={onSubmit}
      schema={additionalChargeFormSchema(maxAmount, t)}
    >
      <ChargeModalFormElements
        amount={lead.order?.securityDeposit ?? 0}
        amountLabel={t(
          'pageOrder.transactions.actions.additionalCharge.amount',
        )}
        currency={lead.order?.currency ?? ''}
        onCancel={onCancel}
        rowDescription={t(
          'pageOrder.transactions.actions.additionalCharge.option',
        )}
      />
    </FormWithProvider>
  );
};

export default AdditionalChargeModal;
